import { getDataroomTenantConfigs } from '@/dataroom/domain/config';
import drsUrl from '@drs/url';
import { appendSearchParams, buildUrl } from '@framework/url/helpers';
import usersUrl from '@users/url';
import dmPortalUrl from '@dm-portal/url';
import CallbackUrl from '@framework/url/CallbackUrl';
import { DataroomTenant } from '@dataroom/tenant/DataroomTenant';
import finsightWebUrl from '@/finsight/infrastructure/finsightWebUrl';
import evercallUrl from '@evercall/url';
import { DataroomUrl as DataroomUrlBase } from '@dataroom/url';
import appendConsentParams from '@framework/cookie-consent/appendConsentParams';

export class DataroomURL extends DataroomUrlBase {
  public constructor(tenant: DataroomTenant | ':tenant') {
    super(tenant);
  }

  public getUploadFileUrl(): string {
    return this.getApiUrl('/api/v1/files');
  }

  public getFormDataUploadFileUrl(): string {
    return `${ this.getUploadFileUrl() }/multipart-save-file`;
  }

  public getUsersDownloadTemplateUrl(dataroomName: string): string {
    return this.getApiUrl(`/api/v1/${ this.getTenantConfig().options.tenantPathName }/${ dataroomName }/users/import/download-template`);
  }

  public getUsersDownloadErrorsUrl(dataroomName: string, hash: string): string {
    return this.getApiUrl(`/api/v1/${ this.getTenantConfig().options.tenantPathName }/${ dataroomName }/users/import/download-errors/${ hash }`);
  }

  public getUsersUploadUrl(dataroomName: string) {
    return this.getApiUrl(`/api/v1/${ this.getTenantConfig().options.tenantPathName }/${ dataroomName }/users/import/parse`);
  }

  public getBulkUploadTemplateUrl(dataroomId: number): string {
    return this.getApiUrl(`/api/v1/qna/bulk-upload/template/download?dataroom_id=${ dataroomId }`);
  }

  public getQnABulkUploadUrl(): string {
    return this.getApiUrl('/api/v1/qna/bulk-upload/upload');
  }

  public getDownloadFileUrl(dataroomName: string, fileId: number): string {
    return this.getApiUrl(
      `/api/v1/${ this.getTenantConfig().options.tenantPathName }/${ dataroomName }/files/${ fileId }/download`,
    );
  }

  public getDownloadOriginalFileUrl(dataroomName: string, fileId: number): string {
    return `${ this.getDownloadFileUrl(dataroomName, fileId) }-original`;
  }

  public getStreamFileUrl(dataroomName: string, fileId: number): string {
    return this.getApiUrl(
      `/api/v1/${ this.getTenantConfig().options.tenantPathName }/${ dataroomName }/files/${ fileId }/stream`,
    );
  }

  public getPrintFileUrl(dataroomName: string, fileId: number): string {
    return this.getApiUrl(
      `/api/v1/${ this.getTenantConfig().options.tenantPathName }/${ dataroomName }/files/${ fileId }/print`,
    );
  }

  public getDownloadExportByHashUrl(dataroomName: string, hash: string): string {
    return this.getDownloadByHashUrl(dataroomName, 'basic-export-report', hash);
  }

  public getDownloadArchiveByHashUrl(dataroomName: string, hash: string): string {
    return this.getDownloadByHashUrl(dataroomName, 'archives/build-archive', hash);
  }

  public getDownloadByHashUrl(dataroomName: string, type: string, hash: string): string {
    return this.getApiUrl(
      `/api/v1/${ this.getTenantConfig().options.tenantPathName }/${ dataroomName }/${ type }/${ hash }/download`,
    );
  }

  public getFollowPath(dataroomId: number | ':dataroomId'): string {
    return `/${ this.tenant }/auto_send_follow/${ dataroomId }`;
  }

  public getAccessRequestPath(dataroomId: number | ':dataroomId', dataroomName: string): string {
    return `/${ this.tenant }/auto_send_access_request/${ dataroomId }/${ dataroomName }`;
  }

  public getFollowUrl(dataroomId: number): string {
    return dmPortalUrl.getUrl(this.getFollowPath(dataroomId));
  }

  public getAccessRequestUrl(dataroomId: number, dataroomDisplayName: string): string {
    return dmPortalUrl.getUrl(
      this.getAccessRequestPath(dataroomId, encodeURIComponent(dataroomDisplayName)),
    );
  }

  public getPreviewerPasServiceUrl(dataroomName: string, fileId: number): string {
    return this.getBaseUrl(
      `/api/v1/${ this.getTenantConfig().options.tenantPathName }/${ dataroomName }/files/${ fileId }/pas-service`,
    );
  }

  public getPreviewUrl(dataroomName: string, fileId: number): string {
    return this.getBaseUrl(
      `api/v1/${ this.getTenantConfig().options.tenantPathName }/${ dataroomName }/files/preview?fileId=${ fileId }`,
    );
  }

  public getTranslationViewUrl(dataroomName: string, fileId: number, languageCode: string): string {
    return this.getBaseUrl(
      `api/v1/${ this.getTenantConfig().options.tenantPathName }/${ dataroomName }/doc-translation/translation/view?fileId=${ fileId }&language=${ languageCode }`,
    );
  }

  public getTranslationDownloadUrl(dataroomName: string, fileId: number, languageCode: string): string {
    return this.getBaseUrl(
      `api/v1/${ this.getTenantConfig().options.tenantPathName }/${ dataroomName }/doc-translation/translation/download?fileId=${ fileId }&language=${ languageCode }`,
    );
  }

  public getOriginalPreviewUrl(dataroomName: string, fileId: number): string {
    return this.getBaseUrl(
      `api/v1/${ this.getTenantConfig().options.tenantPathName }/${ dataroomName }/redaction/get-original?fileId=${ fileId }`,
    );
  }

  public getLoginUrl(callbackUrl: string, errorCode: string | number | null = null): string {
    const {
      protocol,
      hostname,
      pathname,
    } = new URL(usersUrl.getLoginUrl());

    return appendConsentParams(appendSearchParams(buildUrl(protocol, hostname, pathname), {
      callbackUrl,
      tenant: this.tenant,
      ...(errorCode ? { error_code: errorCode } : {}),
    }));
  }

  public getLoginUrlWithCallBackToDmPortal(): string {
    return this.getLoginUrl(dmPortalUrl.getUrl(`/${ this.tenant }`));
  }

  public getLogoutUrl(callbackUrl: string = this.getBaseUrl()): string {
    return appendSearchParams(this.getUrl('/proxy/users', '/api/session/logout'), {
      callbackUrl,
    });
  }

  public getRegisterUrl(callbackUrl: string = CallbackUrl.fromUrl()): string {
    const {
      protocol,
      hostname,
      pathname,
    } = new URL(usersUrl.getRegisterUrl());

    return appendSearchParams(buildUrl(protocol, hostname, pathname), {
      callbackUrl,
      tenant: this.tenant,
    });
  }

  static getEvercallUrl() {
    return evercallUrl.getUrl();
  }

  static getContactUsUrl() {
    return finsightWebUrl.getContactUsUrl();
  }

  static getFinsightWebUrl(...uris: (string | string[])[]) {
    return finsightWebUrl.getUrl(...uris);
  }

  static getComplianceUrl() {
    return finsightWebUrl.getComplianceUrl();
  }

  static getPrivacyPolicyUrl() {
    return finsightWebUrl.getPrivacyPolicyUrl();
  }

  static getSecurityPolicyUrl() {
    return finsightWebUrl.getSecurityPolicyUrl();
  }

  static getTermsOfUseUrl() {
    return finsightWebUrl.getTermsOfUseUrl();
  }

  static getAboutUrl() {
    return finsightWebUrl.getAboutUrl();
  }

  static getCareersUrl() {
    return finsightWebUrl.getCareersUrl();
  }

  static getOurProductsUrl() {
    return finsightWebUrl.getOurProductsUrl();
  }

  static getDrsUrl() {
    return drsUrl.getUrl();
  }

  static getTenantUrlPrefixes(): Set<string> {
    return getDataroomTenantConfigs()
      .reduce(
        (acc: Set<string>, dataroomConfig) => acc.add(dataroomConfig.options.tenantUrlPrefix),
        new Set<string>(),
      );
  }
}

export default (tenant: DataroomTenant | ':tenant') => new DataroomURL(tenant);
